import React, { useMemo, useState } from "react";

import FilterIcon from "@uui/FilterLines";
import { difference, isEmpty } from "lodash/fp";

import SidebarTab from "@app/analytics/components/reports/SidebarTab";
import SidebarTabs from "@app/analytics/components/reports/SidebarTabs";
import { SidebarHeader } from "@app/analytics/components/reports/styles";
import HighlightFilterMenu, {
  HIGHLIGHT_FILTERS,
} from "@app/analytics/MeetingReport/components/HighlightFilterMenu";
import useMeetingReportHighlights from "@app/analytics/MeetingReport/hooks/useMeetingReportHighlights";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import MeetingReportChapters from "@app/analytics/MeetingReport/MeetingReportChapters";
import MeetingReportHighlights from "@app/analytics/MeetingReport/MeetingReportHighlights";
import { FilterCount } from "@app/analytics/MeetingReport/styles";
import type { RecapSidebarSection } from "@app/analytics/MeetingReport/types";
import type { SessionHighlight } from "@app/common/types";

import IconButton from "@rui/IconButton";

const HighlightsAndChapters = () => {
  const { highlights } = useMeetingReportHighlights();
  const {
    summary: { chapters },
  } = useTranscript();
  const {
    data: { isRestrictedReport, shouldUpsellTranscriptEntities },
  } = useSessionMetadata();

  const [selectedTab, setSelectedTab] =
    useState<RecapSidebarSection>("highlights");
  const [excludedHighlightTypes, setExcludedHighlightTypes] = useState<
    SessionHighlight["type"][]
  >([]);

  const handleChangeTab = (
    _e: React.SyntheticEvent,
    value: RecapSidebarSection
  ) => setSelectedTab(value);

  const highlightsToShow = useMemo(() => {
    if (isEmpty(excludedHighlightTypes)) return highlights;
    return highlights.filter(
      (highlight) => !excludedHighlightTypes.includes(highlight.type)
    );
  }, [highlights, excludedHighlightTypes]);

  const filterCount = HIGHLIGHT_FILTERS.reduce((acc, curr) => {
    const typesExcluded = isEmpty(
      difference(curr.types, excludedHighlightTypes)
    );
    return acc + (typesExcluded ? 1 : 0);
  }, 0);

  const handleRemoveFilters = () => setExcludedHighlightTypes([]);

  const onHighlightsTab = selectedTab === "highlights";
  const showFilterMenu =
    onHighlightsTab && !isEmpty(highlights) && !isRestrictedReport;

  return (
    <>
      <SidebarHeader>
        <SidebarTabs value={selectedTab} onChange={handleChangeTab}>
          <SidebarTab
            count={highlights.length}
            label="Highlights"
            value="highlights"
            locked={shouldUpsellTranscriptEntities}
            tooltip={{
              title: "Upgrade to view highlights",
              show: shouldUpsellTranscriptEntities,
            }}
          />
          <SidebarTab
            count={chapters.length}
            label="Chapters"
            value="chapters"
            locked={shouldUpsellTranscriptEntities}
            tooltip={{
              title: "Upgrade to view chapters",
              show: shouldUpsellTranscriptEntities,
            }}
          />
        </SidebarTabs>
        {showFilterMenu && (
          <HighlightFilterMenu
            excludedTypes={excludedHighlightTypes}
            filterCount={filterCount}
            onFilterChange={setExcludedHighlightTypes}
          >
            <IconButton size="small" sx={{ minHeight: "28px" }}>
              <FilterIcon sx={{ height: "16px", width: "16px" }} />
              {filterCount > 0 && <FilterCount>{filterCount}</FilterCount>}
            </IconButton>
          </HighlightFilterMenu>
        )}
      </SidebarHeader>
      {onHighlightsTab ? (
        <MeetingReportHighlights
          filtered={!isEmpty(excludedHighlightTypes)}
          highlights={highlightsToShow}
          onRemoveFilters={handleRemoveFilters}
          sx={{ mt: 1 }}
        />
      ) : (
        <MeetingReportChapters chapters={chapters} sx={{ mt: 1 }} />
      )}
    </>
  );
};

export default HighlightsAndChapters;
